import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  CircularProgress,
  Box,
  InputAdornment,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import { colors } from "../../../../theme/colors";
import {
  MainWrapper,
  HeaderSection,
  HeaderTitle,
  HeaderSubtitle,
  HeaderChip,
  CardPaper,
  IconBox,
  CardTitle,
  CardSubtitle,
  StyledTextField,
  SubmitButton,
  CompanyListPaper,
  CompanyChipItem,
} from "./style";
import FieldErrorMessage from "../../../../components/common-components/FieldErrorMessage";
import useCreateRoles from "./useCreateRoles";
import { axiosInstance } from "../../../../utils/axiosInstance";
import { CompanyEndPoint } from "../../../../api-routes";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";

const CreateRole = () => {
  const { formik, isCreating } = useCreateRoles();

  const { showToast } = useToast();
  const [companyName, setCompanyName] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState("");

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axiosInstance.get(CompanyEndPoint.GET_ALL_COMPANIES);
      setCompanies(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) {
      setCompanyError("Company name is required");
      return;
    }
    setCompanyError("");
    setCompanyLoading(true);
    try {
      await axiosInstance.post(CompanyEndPoint.CREATE_COMPANY, {
        name: companyName.trim(),
      });
      setCompanyName("");
      showToast("Company created successfully");
      fetchCompanies();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create company";
      setCompanyError(msg);
      showToast(msg, "error");
    } finally {
      setCompanyLoading(false);
    }
  };

  return (
    <MainWrapper>
      <HeaderSection>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <HeaderTitle variant="h5">
            Role & Organization Management
          </HeaderTitle>
          {companies.length > 0 && (
            <HeaderChip label={`${companies.length} Companies`} size="small" />
          )}
        </Box>
        <HeaderSubtitle variant="body2">
          Configure candidate roles and company client requisitions for your organization workspace.
        </HeaderSubtitle>
      </HeaderSection>

      <Grid container spacing={3} sx={{ maxWidth: 1100 }}>
        <Grid item xs={12} md={6}>
          <CardPaper elevation={0}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <IconBox>
                  <BadgeOutlinedIcon sx={{ fontSize: "1.35rem" }} />
                </IconBox>
                <Box>
                  <CardTitle variant="h6">Create Role</CardTitle>
                  <CardSubtitle variant="body2">
                    Add candidate roles and designations used in job postings.
                  </CardSubtitle>
                </Box>
              </Box>

              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <StyledTextField
                      size="small"
                      placeholder="e.g. Senior Frontend Developer"
                      variant="outlined"
                      id="role"
                      name="role"
                      value={formik.values.role}
                      onChange={formik.handleChange}
                      disabled={isCreating}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkOutlineIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FieldErrorMessage error={formik.errors.role} />
                  </Box>

                  <SubmitButton
                    variant="contained"
                    type="submit"
                    startIcon={!isCreating ? <AddIcon /> : null}
                    disabled={isCreating || !formik.values.role.trim()}
                  >
                    {isCreating ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        Creating <CircularProgress size={18} color="inherit" />
                      </Box>
                    ) : (
                      "Create Role"
                    )}
                  </SubmitButton>
                </Stack>
              </form>
            </Box>
          </CardPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardPaper elevation={0}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <IconBox>
                  <BusinessOutlinedIcon sx={{ fontSize: "1.35rem" }} />
                </IconBox>
                <Box>
                  <CardTitle variant="h6">Create Company / Client</CardTitle>
                  <CardSubtitle variant="body2">
                    Add company or client names for job requisitions.
                  </CardSubtitle>
                </Box>
              </Box>

              <form onSubmit={handleCreateCompany}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <StyledTextField
                      size="small"
                      placeholder="e.g. Acme Corporation"
                      variant="outlined"
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        setCompanyError("");
                      }}
                      disabled={companyLoading}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DomainOutlinedIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {companyError && <FieldErrorMessage error={companyError} />}
                  </Box>

                  <SubmitButton
                    variant="contained"
                    type="submit"
                    startIcon={!companyLoading ? <AddIcon /> : null}
                    disabled={companyLoading || !companyName.trim()}
                  >
                    {companyLoading ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        Creating <CircularProgress size={18} color="inherit" />
                      </Box>
                    ) : (
                      "Add Company"
                    )}
                  </SubmitButton>
                </Stack>
              </form>
            </Box>
          </CardPaper>
        </Grid>

        {companies.length > 0 && (
          <Grid item xs={12}>
            <CompanyListPaper elevation={0}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DomainOutlinedIcon sx={{ color: colors.primary, fontSize: "1.2rem" }} />
                  <CardTitle variant="subtitle1">
                    Configured Companies ({companies.length})
                  </CardTitle>
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {companies.map((comp) => (
                  <CompanyChipItem
                    key={comp._id || comp.id || comp.name}
                    label={comp.name}
                    variant="outlined"
                  />
                ))}
              </Box>
            </CompanyListPaper>
          </Grid>
        )}
      </Grid>
    </MainWrapper>
  );
};

export default CreateRole;
